import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { usePostHog } from 'posthog-js/react'

// Import step components
import GuideStep from "../components/onboarding/GuideStep";
import UploadVideosStep from "../components/onboarding/UploadVideosStep";
import ProcessingStep from "../components/onboarding/ProcessingStep";
import AppUrlLoginStep from "../components/onboarding/AppUrlLoginStep";
import StripePaymentStep from "../components/onboarding/StripePaymentStep";
import SlackSupportStep from "../components/onboarding/SlackSupportStep";
import SuccessStep from "../components/onboarding/SuccessStep";
import OnboardingProgress from "../components/onboarding/OnboardingProgress";
// Onboarding flow configuration
const ONBOARDING_STEPS = {
  GUIDE: 1,
  UPLOAD_VIDEOS: 2,
  PROCESSING: 3,
  APP_URL_LOGIN: 4,
  SLACK_SUPPORT: 5,
  STRIPE_PAYMENT: 6,
  SUCCESS: 7
};

const STEP_NAMES = {
  [ONBOARDING_STEPS.GUIDE]: "Guide",
  [ONBOARDING_STEPS.UPLOAD_VIDEOS]: "Upload Videos",
  [ONBOARDING_STEPS.PROCESSING]: "Processing",
  [ONBOARDING_STEPS.APP_URL_LOGIN]: "App Setup",
  [ONBOARDING_STEPS.SLACK_SUPPORT]: "Support",
  [ONBOARDING_STEPS.STRIPE_PAYMENT]: "Payment",
  [ONBOARDING_STEPS.SUCCESS]: "Complete"
};

// Secure onboarding state management
const OnboardingStateManager = {
  getCurrentStep: () => {
    try {
      const state = JSON.parse(localStorage.getItem('onboardingState') || '{}');
      return state.currentStep || ONBOARDING_STEPS.GUIDE;
    } catch {
      return ONBOARDING_STEPS.GUIDE;
    }
  },
  
  setCurrentStep: (step) => {
    const state = JSON.parse(localStorage.getItem('onboardingState') || '{}');
    state.currentStep = step;
    state.completedSteps = state.completedSteps || [];
    if (!state.completedSteps.includes(step - 1) && step > 1) {
      // Don't allow skipping steps
      return false;
    }
    if (step === ONBOARDING_STEPS.SUCCESS) {
      state.completedSteps = [1, 2, 3, 4, 5, 6];
    } else if (!state.completedSteps.includes(step)) {
      state.completedSteps.push(step);
    }
    localStorage.setItem('onboardingState', JSON.stringify(state));
    return true;
  },
  
  isStepAccessible: (step) => {
    const state = JSON.parse(localStorage.getItem('onboardingState') || '{}');
    const completedSteps = state.completedSteps || [];
    
    // First step is always accessible
    if (step === ONBOARDING_STEPS.GUIDE) return true;
    
    // For other steps, check if previous step is completed
    return completedSteps.includes(step - 1);
  },
  
  markStepCompleted: (step) => {
    const state = JSON.parse(localStorage.getItem('onboardingState') || '{}');
    state.completedSteps = state.completedSteps || [];
    if (!state.completedSteps.includes(step)) {
      state.completedSteps.push(step);
    }
    localStorage.setItem('onboardingState', JSON.stringify(state));
  },
  
  reset: () => {
    localStorage.removeItem('onboardingState');
  },
  
  isComplete: () => {
    const state = JSON.parse(localStorage.getItem('onboardingState') || '{}');
    return state.completedSteps && state.completedSteps.includes(ONBOARDING_STEPS.SUCCESS);
  }
};

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(ONBOARDING_STEPS.GUIDE);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const userData = location.state || JSON.parse(localStorage.getItem('onboardingUserData') || '{}');
  const posthog = usePostHog();

  // Security: Validate step access and redirect if necessary
  useEffect(() => {
    const savedStep = OnboardingStateManager.getCurrentStep();
    
    // Check if user is returning from Stripe payment
    const urlParams = new URLSearchParams(window.location.search);
    const fromStripe = urlParams.has('user_email') || urlParams.has('payment_success');
    
    // Handle URL-based step detection with validation
    let requestedStep = savedStep;
    
    // If returning from Stripe, check if payment was successful and advance
    if (fromStripe && savedStep >= ONBOARDING_STEPS.STRIPE_PAYMENT) {
      // Clean up URL parameters first
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      
      // Check if we should advance to next step (payment completed)
      const pendingData = localStorage.getItem('pendingOnboardingData');
      if (pendingData) {
        // Payment was initiated, let StripePaymentStep handle verification
        requestedStep = ONBOARDING_STEPS.STRIPE_PAYMENT;
      } else {
        // No pending data means payment might have been completed already
        // Advance to next step
        requestedStep = Math.min(ONBOARDING_STEPS.SUCCESS, savedStep + 1);
        OnboardingStateManager.markStepCompleted(ONBOARDING_STEPS.STRIPE_PAYMENT);
      }
    }
    
    // Validate step access
    if (!OnboardingStateManager.isStepAccessible(requestedStep)) {
      // Redirect to the appropriate step
      const validStep = OnboardingStateManager.getCurrentStep();
      setCurrentStep(validStep);
      
      // Update URL to match the valid step without exposing internal step logic
      if (validStep === ONBOARDING_STEPS.GUIDE) {
        navigate('/onboarding', { replace: true });
      }
      return;
    }
    
    // Update step if valid
    if (OnboardingStateManager.setCurrentStep(requestedStep)) {
      setCurrentStep(requestedStep);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!posthog) return;
    if (userData?.email) {
      posthog.identify(userData.email, {
        email: userData.email,
        full_name: userData.fullName,
        company_name: userData.companyName,
        position: userData.position,
      });
    }
  }, [posthog, userData?.email, userData?.fullName, userData?.companyName, userData?.position]);

  useEffect(() => {
    if (!posthog) return;
    const stepName = STEP_NAMES[currentStep] || `Step ${currentStep}`;
    posthog.capture('onboarding_step_arrived', {
      step_number: currentStep,
      step_name: stepName,
      email: userData?.email || undefined,
    });
  }, [posthog, currentStep, userData?.email]);

  useEffect(() => {
    // Set onboarding flag when component mounts
    localStorage.setItem('inOnboarding', 'true');
    
    // Check if onboarding is already complete
    if (OnboardingStateManager.isComplete()) {
      localStorage.setItem('inOnboarding', 'false');
      navigate('/');
      return;
    }
    
    // Check if user just returned from Stripe and has subscription
    const checkAndAdvanceAfterPayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const fromStripe = urlParams.has('user_email');
      
      if (fromStripe && currentStep === ONBOARDING_STEPS.STRIPE_PAYMENT) {
        // Small delay to let component initialize
        setTimeout(async () => {
          try {
            // Import supabase and API_URL here to avoid circular dependencies
            const { supabase } = await import('../supabaseClient');
            const { API_URL } = await import('../globals');
            
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) {
              const response = await fetch(`${API_URL}/user/check_subscription`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${session.access_token}`
                },
              });

              if (response.ok) {
                const data = await response.json();
                if (data.subscribed) {
                  // User has subscription, advance to next step
                  OnboardingStateManager.markStepCompleted(ONBOARDING_STEPS.STRIPE_PAYMENT);
                  const nextStep = ONBOARDING_STEPS.SUCCESS;
                  if (OnboardingStateManager.setCurrentStep(nextStep)) {
                    setCurrentStep(nextStep);
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error checking subscription:', error);
          }
        }, 1000);
      }
    };
    
    checkAndAdvanceAfterPayment();
  }, [navigate, currentStep]);

  const advanceToNextStep = (data = null) => {
    // Store uploaded videos when coming from upload step
    if (currentStep === ONBOARDING_STEPS.UPLOAD_VIDEOS && data) {
      setUploadedVideos(data);
    }

    const nextStep = currentStep + 1;
    OnboardingStateManager.markStepCompleted(currentStep);
    if (OnboardingStateManager.setCurrentStep(nextStep)) {
      setCurrentStep(nextStep);
    }
  };

  const goBackToPreviousStep = () => {
    const previousStep = currentStep - 1;
    if (previousStep >= ONBOARDING_STEPS.GUIDE) {
      // Don't mark the current step as completed when going back
      if (OnboardingStateManager.setCurrentStep(previousStep)) {
        setCurrentStep(previousStep);
      }
    }
  };

  const handleComplete = () => {
    // Mark onboarding as complete
    OnboardingStateManager.markStepCompleted(ONBOARDING_STEPS.SUCCESS);
    
    // Remove onboarding flags and user data when onboarding is complete
    localStorage.setItem('inOnboarding', 'false');
    localStorage.removeItem('onboardingUserData');
    
    // Clean up onboarding state
    OnboardingStateManager.reset();
    
    navigate('/');
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case ONBOARDING_STEPS.GUIDE:
        return <GuideStep onComplete={advanceToNextStep} />;
      
      case ONBOARDING_STEPS.UPLOAD_VIDEOS:
        return <UploadVideosStep onComplete={advanceToNextStep} />;
      
      case ONBOARDING_STEPS.PROCESSING:
        return <ProcessingStep onComplete={advanceToNextStep} onGoBack={goBackToPreviousStep} uploadedVideos={uploadedVideos} />;
      
      case ONBOARDING_STEPS.APP_URL_LOGIN:
        return <AppUrlLoginStep onComplete={advanceToNextStep} />;
      
      case ONBOARDING_STEPS.SLACK_SUPPORT:
        return <SlackSupportStep onComplete={advanceToNextStep} userData={userData} />;
      
      case ONBOARDING_STEPS.STRIPE_PAYMENT:
        return <StripePaymentStep onComplete={advanceToNextStep} />;
      
      case ONBOARDING_STEPS.SUCCESS:
        return <SuccessStep onComplete={handleComplete} />;
      
      default:
        return <GuideStep onComplete={advanceToNextStep} />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: "100vh",
        maxHeight: "100vh",
        backgroundColor: "#121212",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
          py: 4,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "800px",
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {renderCurrentStep()}
        </Box>
      </Container>
      
      {/* Progress tracker is hidden for clean Apple-style design */}
      <OnboardingProgress currentStep={currentStep} visible={false} />
    </Box>
  );
}
